import { useCallback, useRef } from 'react'
import { sortCriteria } from '../context/engines/commerceEngine'
import { getSoftwarePreferences } from '../api'
import { getFragment } from '../shared/coveoUtils'
import { SEARCH_SOURCE_MAP, IN_STOCK_STATUS_FIELD, INSTOCK_ONLY_KEY, FACET_TYPE_HIERARCHICAL } from '../constants'

/**
 * Product search actions — wraps the commerce engine controllers to provide the
 * imperative API consumed by SearchContext and child components.
 *
 * @param {object} params
 * @param {object}  params.activeCore            - Coveo search or productListing controller
 * @param {object}  params.activeSort            - Sort controller
 * @param {object}  params.activePagination      - Pagination controller
 * @param {object}  params.activeFacetGenerator  - FacetGenerator controller
 * @param {object}  params.activeUrlManager      - Coveo urlManager controller (search or listing)
 * @param {object}  params.searchBox             - SearchBox controller
 * @param {boolean} params.isListingPage         - true on product-listing pages
 * @param {boolean} params.isLoggedIn            - true when the user is authenticated
 *
 * @returns {{ redirectAndTrack, searchProducts, searchHandler, setFilter, clearFilters }}
 */
export function useProductSearch({
  activeCore,
  activeSort,
  activePagination,
  activeFacetGenerator,
  activeUrlManager,
  searchBox,
  isListingPage,
  isLoggedIn,
}) {
  const hasExecutedFirstSearch = useRef(false)
  /**
   * Fires analytics events then redirects to the given URL.
   * Used when Coveo returns a query redirect trigger.
   */
  const redirectAndTrack = useCallback((redirectURL) => {
    if (!redirectURL) return
    const urlParams = new URLSearchParams(window.location.search)
    const query = urlParams.get('q') || ''
    const qsrc = urlParams.get('qsrc') || ''
    const searchType = SEARCH_SOURCE_MAP[qsrc] || 'Typed In'
    const queryInfo = { queryKeyword: query, queryType: searchType, queryRedirect: redirectURL }
    window.fireTagEvent && window.fireTagEvent('queryInfo', queryInfo)
    localStorage.setItem('search-type', searchType)
    window._satellite && window._satellite.track && window._satellite.track('queryInfo')
    window.location = redirectURL
  }, [])

  /**
   * Seeds the IN_STOCK_STATUS_FIELD facet with an explicit "true" value before the
   * first search fires, so Coveo's first response already reflects the correct
   * "Show in stock only" state — mirrors app-search's URL-param-first, user-preference-
   * fallback resolution:
   *   1. If the URL already has an explicit f-<field> facet param, do nothing — the
   *      urlManager already restored it into facet state when the engine was constructed.
   *   2. Otherwise, resolve the value from the "displayInStockOnly" software preference
   *      for logged-in users. Only when that preference is true do we synchronize the
   *      urlManager with an explicit f-<field>=true param — this seeds facet state AND
   *      fires the (first) request. When the preference is false (or absent/not logged
   *      in/fetch fails), we do nothing and let the caller fire the normal first search.
   *
   * Returns true if it fired the request itself (via urlManager.synchronize), so the
   * caller can skip its own executeFirstSearch/executeFirstRequest call.
   */
  const applyInitialInStockOverride = useCallback(async () => {
    const hasExplicitUrlValue = new URLSearchParams(window.location.search).has(`f-${IN_STOCK_STATUS_FIELD}`)
    if (hasExplicitUrlValue) return false

    let displayInStockOnly = false
    if (isLoggedIn) {
      try {
        const preferences = await getSoftwarePreferences()
        displayInStockOnly = !!preferences?.displayInStockOnly
      } catch {
        displayInStockOnly = false
      }
    }

    if (!displayInStockOnly) return false

    const baseFragment = getFragment()
    const overrideFragment = baseFragment
      ? `${baseFragment}&f-${IN_STOCK_STATUS_FIELD}=${displayInStockOnly}`
      : `f-${IN_STOCK_STATUS_FIELD}=${displayInStockOnly}`
    activeUrlManager.synchronize(overrideFragment)
    return true
  }, [isLoggedIn, activeUrlManager])

  /**
   * Fires the initial search or listing request (idempotent — no-op if already fired).
   */
  const searchProducts = useCallback(async () => {
    if (hasExecutedFirstSearch.current) return
    if (activeCore.state.responseId || activeCore.state.isLoading) return

    hasExecutedFirstSearch.current = true

    if (!isListingPage) {
      const qFromQuery = new URLSearchParams(window.location.search).get('q') || ''
      if (qFromQuery && searchBox) {
        searchBox.updateText(qFromQuery)
      }
    }

    const appliedOverride = await applyInitialInStockOverride()
    if (appliedOverride) return

    if (isListingPage) {
      activeCore.executeFirstRequest()
      return
    }
    activeCore.executeFirstSearch()
  }, [activeCore, isListingPage, searchBox, applyInitialInStockOverride])

  /**
   * Handles search-parameter changes dispatched by pagination, sort, and page-size controls.
   * Also handles the instockOnly facet toggle for the ini_in_stock_status_sales_org field.
   * key: 'rows' | 'customSort' | 'start' | 'instockOnly'
   */
  const searchHandler = useCallback(({ key, value }) => {
    switch (key) {
      case 'rows':
        activePagination.setPageSize(Number(value))
        break
      case 'customSort':
        activeSort.sortBy(sortCriteria[value] || sortCriteria.best_match)
        break
      case 'start': {
        const perPage = activePagination.state.pageSize || 50
        const targetPage = Math.floor(Number(value) / perPage)
        activePagination.selectPage(targetPage)
        break
      }
      case INSTOCK_ONLY_KEY: {
        // Handle the "Show in stock only" custom facet — select/deselect 'true' on
        // IN_STOCK_STATUS_FIELD. deselectAll()/toggleSelect() each fire their own
        // request, so only chain them when a real conflicting value is selected.
        const inStockFacet = activeFacetGenerator.facets.find(
          (f) => f.state.field === IN_STOCK_STATUS_FIELD
        )
        if (inStockFacet && inStockFacet.type !== FACET_TYPE_HIERARCHICAL) {
          const trueValue = inStockFacet.state.values.find((v) => v.value === 'true')
          if (trueValue) {
            if (value) {
              // Turning on: no-op if already selected; clear first only on a real conflict
              if (trueValue.state !== 'selected') {
                const hasConflictingSelection = inStockFacet.state.values.some(
                  (v) => v.value !== 'true' && v.state === 'selected'
                )
                if (hasConflictingSelection) {
                  inStockFacet.deselectAll()
                }
                inStockFacet.toggleSelect(trueValue)
              }
            } else if (inStockFacet.state.values.some((v) => v.state === 'selected')) {
              // Turning off: only clear when something is actually selected
              inStockFacet.deselectAll()
            }
          }
        }
        break
      }
      default:
        break
    }
  }, [activePagination, activeSort, activeFacetGenerator])

  /**
   * Toggles a facet value.  Handles both regular and hierarchical (category) facets.
   */
  const setFilter = useCallback(({ group, val }) => {
    if (!group || !val) return
    const facetController = activeFacetGenerator.facets.find(
      (f) => f.state.field === group
    )
    if (!facetController) return

    // Regular facets — value lives in state.values
    if (facetController.type !== FACET_TYPE_HIERARCHICAL) {
      const value = facetController.state.values.find((v) => v.value === val)
      if (value) facetController.toggleSelect(value)
      return
    }

    // Hierarchical (category) facets — navigate the ancestry
    const ancestry = facetController.state.selectedValueAncestry
    if (!ancestry || ancestry.length === 0) return

    const index = ancestry.findIndex((crumb) => crumb.value === val)
    if (index === -1) return
    const isLast = index === ancestry.length - 1

    if (isLast) {
      // Clicking the deepest selected category — step up one level or clear entirely
      if (ancestry.length > 1) {
        facetController.toggleSelect(ancestry[ancestry.length - 2])
      } else {
        facetController.deselectAll()
      }
    } else {
      // Clicking a higher-level ancestor — navigate directly to that level
      facetController.toggleSelect(ancestry[index])
    }
  }, [activeFacetGenerator])

  /** Clears all active product facets. */
  const clearFilters = useCallback(() => {
    activeFacetGenerator.deselectAll()
  }, [activeFacetGenerator])

  return { redirectAndTrack, searchProducts, searchHandler, setFilter, clearFilters }
}
